// src/pages/Campaigns/EventPost/GalleryDialog.js

import React, { useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Grid, Typography, IconButton, Slider, FormControlLabel, Switch,
  TextField, Box, Paper, CircularProgress, Alert, Tabs, Tab
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  DragIndicator as DragIcon,
  VideoLibrary as VideoIcon,
  Image as ImageIcon
} from '@mui/icons-material';

const GalleryDialog = ({ open, onClose, onInsert, postId, axiosInstance, chatbotId }) => {
  const [images, setImages] = useState([]);
  const [columns, setColumns] = useState(3);
  const [enableLightbox, setEnableLightbox] = useState(true);
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Video URL input
  const [videoUrl, setVideoUrl] = useState('');
  const [addingVideo, setAddingVideo] = useState(false);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      files.forEach(file => formData.append('images', file));

      const response = await axiosInstance.post(
        `/api/event-posts/${postId}/upload-gallery-images`,
        formData,
        {
          params: { chatbot_id: chatbotId },
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (response.data.success) {
        setImages(prev => [...prev, ...response.data.items]);
      } else {
        setError(response.data.message || 'Failed to upload images');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload images. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const parseVideoUrl = (url) => {
    // YouTube patterns
    const youtubePatterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
    ];

    for (const pattern of youtubePatterns) {
      const match = url.match(pattern);
      if (match) {
        return {
          media_type: 'YOUTUBE',
          video_id: match[1],
          embed_url: `https://www.youtube.com/embed/${match[1]}`,
          thumbnail_url: `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`
        };
      }
    }

    // Vimeo pattern
    const vimeoPattern = /vimeo\.com\/(\d+)/;
    const vimeoMatch = url.match(vimeoPattern);
    if (vimeoMatch) {
      return {
        media_type: 'VIMEO',
        video_id: vimeoMatch[1],
        embed_url: `https://player.vimeo.com/video/${vimeoMatch[1]}`,
        thumbnail_url: `https://vumbnail.com/${vimeoMatch[1]}.jpg`
      };
    }

    return null;
  };

  const handleAddVideo = () => {
    if (!videoUrl.trim()) return;

    setAddingVideo(true);
    setError(null);

    const videoData = parseVideoUrl(videoUrl);
    if (!videoData) {
      setError('Invalid video URL. Please use YouTube or Vimeo links.');
      setAddingVideo(false);
      return;
    }

    setImages(prev => [...prev, {
      ...videoData,
      alt_text: `Video ${prev.length + 1}`
    }]);
    setVideoUrl('');
    setAddingVideo(false);
  };

  const moveItem = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= images.length) return;

    const reordered = [...images];
    const [removed] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, removed);
    setImages(reordered);
  };

  const handleRemoveItem = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleCaptionChange = (index, caption) => {
    setImages(prev => prev.map((img, i) =>
      i === index ? { ...img, caption } : img
    ));
  };

  const handleAltTextChange = (index, alt_text) => {
    setImages(prev => prev.map((img, i) =>
      i === index ? { ...img, alt_text } : img
    ));
  };

  const handleCreate = async () => {
    if (images.length === 0) {
      setError('Please add at least one image or video to the gallery.');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const response = await axiosInstance.post(
        `/api/event-posts/${postId}/galleries`,
        {
          title,
          columns: columns.toString(),
          enable_lightbox: enableLightbox,
          items: images.map((img, idx) => ({
            ...img,
            sort_order: idx
          }))
        },
        { params: { chatbot_id: chatbotId } }
      );

      if (response.data.success) {
        onInsert(response.data.shortcode, response.data.gallery);
        handleClose();
      } else {
        setError(response.data.message || 'Failed to create gallery');
      }
    } catch (err) {
      console.error('Create gallery error:', err);
      setError('Failed to create gallery. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setImages([]);
    setColumns(3);
    setEnableLightbox(true);
    setTitle('');
    setError(null);
    setVideoUrl('');
    setActiveTab(0);
    onClose();
  };

  const columnMarks = [
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' },
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Create Gallery
        <IconButton
          sx={{ position: 'absolute', right: 8, top: 8 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Add Media Tabs */}
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
          <Tab icon={<ImageIcon />} label="Upload Images" />
          <Tab icon={<VideoIcon />} label="Add Video" />
        </Tabs>

        {activeTab === 0 && (
          <Paper
            variant="outlined"
            sx={{
              p: 3, mb: 3, textAlign: 'center',
              border: '2px dashed #ccc',
              backgroundColor: '#fafafa',
              cursor: 'pointer',
              '&:hover': { borderColor: '#999', backgroundColor: '#f0f0f0' }
            }}
            component="label"
          >
            <input
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              disabled={uploading}
            />
            {uploading ? (
              <Box sx={{ py: 2 }}>
                <CircularProgress size={40} />
                <Typography sx={{ mt: 2 }}>Uploading images...</Typography>
              </Box>
            ) : (
              <>
                <UploadIcon sx={{ fontSize: 48, color: '#999', mb: 1 }} />
                <Typography variant="h6">Click or drag images here</Typography>
                <Typography variant="caption" color="text.secondary">
                  You can select multiple images at once
                </Typography>
              </>
            )}
          </Paper>
        )}

        {activeTab === 1 && (
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add YouTube or Vimeo videos to your gallery
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Video URL"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                size="small"
              />
              <Button
                variant="contained"
                onClick={handleAddVideo}
                disabled={addingVideo || !videoUrl.trim()}
              >
                Add
              </Button>
            </Box>
          </Paper>
        )}

        {/* Gallery Settings */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Typography gutterBottom>Columns: {columns}</Typography>
            <Slider
              value={columns}
              onChange={(e, v) => setColumns(v)}
              min={1}
              max={4}
              marks={columnMarks}
              step={1}
              valueLabelDisplay="auto"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={enableLightbox}
                  onChange={(e) => setEnableLightbox(e.target.checked)}
                />
              }
              label="Enable lightbox on click"
            />
          </Grid>
        </Grid>

        {/* Optional Title */}
        <TextField
          fullWidth
          label="Gallery Title (Optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 3 }}
          size="small"
          placeholder="e.g., Event Photos, Behind the Scenes"
        />

        {/* Preview Grid */}
        {images.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Gallery Preview ({images.length} items)
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${columns}, 1fr)`,
                gap: 2,
                mb: 2
              }}
            >
              {images.map((item, index) => (
                <Paper
                  key={`${item.url || item.video_id}-${index}`}
                  elevation={2}
                  sx={{ position: 'relative', overflow: 'hidden' }}
                >
                  {/* Drag handles */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 4,
                      left: 4,
                      zIndex: 2,
                      display: 'flex',
                      gap: 0.5
                    }}
                  >
                    <IconButton
                      size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.8)' }}
                      onClick={() => moveItem(index, index - 1)}
                      disabled={index === 0}
                    >
                      <Typography variant="caption">&#8593;</Typography>
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.8)' }}
                      onClick={() => moveItem(index, index + 1)}
                      disabled={index === images.length - 1}
                    >
                      <Typography variant="caption">&#8595;</Typography>
                    </IconButton>
                  </Box>

                  {/* Delete button */}
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      zIndex: 2,
                      bgcolor: 'rgba(255,255,255,0.8)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' }
                    }}
                    onClick={() => handleRemoveItem(index)}
                  >
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>

                  {/* Media preview */}
                  {item.media_type === 'IMAGE' ? (
                    <img
                      src={item.url}
                      alt={item.alt_text || `Image ${index + 1}`}
                      style={{
                        width: '100%',
                        aspectRatio: '4/3',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        aspectRatio: '4/3',
                        position: 'relative',
                        backgroundColor: '#000'
                      }}
                    >
                      <img
                        src={item.thumbnail_url}
                        alt={item.alt_text || `Video ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          bgcolor: 'rgba(0,0,0,0.7)',
                          borderRadius: '50%',
                          width: 50,
                          height: 50,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <VideoIcon sx={{ color: 'white', fontSize: 28 }} />
                      </Box>
                    </Box>
                  )}

                  {/* Caption and alt text */}
                  <Box sx={{ p: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Caption (optional)"
                      value={item.caption || ''}
                      onChange={(e) => handleCaptionChange(index, e.target.value)}
                      sx={{ mb: 1 }}
                      variant="standard"
                    />
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Alt text"
                      value={item.alt_text || ''}
                      onChange={(e) => handleAltTextChange(index, e.target.value)}
                      variant="standard"
                    />
                  </Box>
                </Paper>
              ))}
            </Box>
          </>
        )}

        {images.length === 0 && (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No items added yet. Upload images or add videos above to create a gallery.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={images.length === 0 || creating}
        >
          {creating ? 'Creating...' : `Create Gallery (${images.length} items)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GalleryDialog;
